import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import AvailableAllowanceCard from 'adminDashboard/common/availableAllowanceCard'

import { EEDashboardSummaryDto } from 'core/application/employeeDashboard/perks/getPerksEEDashboardSummary'
import AllowanceFrequency, { AllowanceFrequencyType } from 'core/domain/benefit/allowanceFrequency'

import useCarousel from 'common/hooks/useCarousel'
import { useLoginUserV2 } from 'common/hooks/useLoginUser'

import { Button, Carousel, Col, Icon, Row, Skeleton } from 'common/components'
import { useBreakPoints } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import CardPerksAllowance from './categoryAllowanceCard'

type PerksAllowanceProps = {
  planPeriodId: string
  endDate: string
  categoriesSummary: EEDashboardSummaryDto
  categorySummaryLoading: boolean
}
const PerksAllowance = ({ planPeriodId, categoriesSummary, categorySummaryLoading, endDate }: PerksAllowanceProps) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { user } = useLoginUserV2()
  const { isMd } = useBreakPoints()

  const categoriesSummaryLength = categoriesSummary?.categorySummaries?.length

  const allowanceFrequencies: AllowanceFrequencyType[] = [
    { id: AllowanceFrequency.Monthly, name: 'Monthly' },
    { id: AllowanceFrequency.Quarterly, name: 'Quarterly' },
    { id: AllowanceFrequency.Annual, name: 'Annual' },
  ]
  const { carouselRef, currentSlideIndex, slidesNumber, goToNextSlide, goToPreviousSlide, handleCurrentSlideIndex } =
    useCarousel(categoriesSummaryLength)

  return (
    <>
      <Skeleton active={true} loading={categorySummaryLoading}>
        <Row>
          <Col span={24}>
            <h5>
              {t('ee-dashboard.home.perks-allowances.welcome')}, {user.firstName}
            </h5>
          </Col>
          {categoriesSummary && categoriesSummary?.offerSameAllowance && (
            <Col sm={24} lg={categoriesSummary?.offerSameAllowance ? 15 : 7}>
              <AvailableAllowanceCard
                daysToRunOut={categoriesSummary.categorySummaries[0].daysToRunOut}
                runOutDays={categoriesSummary.categorySummaries[0].runOutDays}
                isPerkContinuous={categoriesSummary.categorySummaries[0].isPerkContinuous}
                startDate={categoriesSummary.categorySummaries[0].startDate}
                endDate={categoriesSummary.categorySummaries[0].endDate}
                offerSameAllowance={categoriesSummary?.offerSameAllowance}
                monthlyAllowance={categoriesSummary.categorySummaries[0].monthlyAllowance}
                planPeriodId={planPeriodId}
                allowanceFrequencies={allowanceFrequencies}
              />
            </Col>
          )}
        </Row>
        <div className="w-100 flex flex__end mb-8">
          <div className="flex">
            <Button
              className="mr-8"
              size="default"
              htmlType="button"
              type="default"
              disabled={currentSlideIndex === 0}
              onClick={goToPreviousSlide}
            >
              <Icon type="fa fa-arrow-left" fontAwesome />
            </Button>
            <Button
              size="default"
              htmlType="button"
              type="default"
              disabled={currentSlideIndex + slidesNumber === categoriesSummaryLength}
              onClick={goToNextSlide}
            >
              <Icon type="fa fa-arrow-right" fontAwesome />
            </Button>
          </div>
        </div>
        {categoriesSummary && (
          <>
            <Row className={'mb-8'}>
              <Col span={24}>
                <div className="color-gray-9 semi-bold font-16">
                  {' '}
                  {t('ee-dashboard.home.perks-allowances.perks-categories-title')} (
                  {categoriesSummary.categorySummaries.length})
                </div>
              </Col>
            </Row>

            <div className="color-gray-7 font-14 mb-16">
              Showing{' '}
              {isMd ? `${currentSlideIndex + 1}-${currentSlideIndex + slidesNumber}` : `${currentSlideIndex + 1}`} of{' '}
              {categoriesSummaryLength} total offers
            </div>

            <Carousel
              carouselRef={carouselRef}
              slidesToShow={categoriesSummaryLength > 3 ? 3.4 : 3}
              slidesToScroll={3}
              infinite={false}
              className="mb-8 m-8-negative"
              dots={false}
              swipeToSlide
              afterChange={handleCurrentSlideIndex}
              responsive={[
                {
                  breakpoint: 480,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                  },
                },
              ]}
            >
              {categoriesSummary?.categorySummaries?.map((item) => (
                <CardPerksAllowance
                  isPerkContinuous={item.isPerkContinuous}
                  runOutDays={item.runOutDays}
                  daysToRunOut={item.daysToRunOut}
                  translate={t}
                  hasEndPerkRunOutDaysEnded={item.hasEndPerkRunOutDaysEnded}
                  icon={item.icon}
                  title={item.name}
                  availableAllowanceTitle={t('ee-dashboard.home.perks-allowances.perk-cards.available-allowance')}
                  availableAllowanceValue={item.availableAllowance}
                  buttonText={t('ee-dashboard.home.perks-allowances.button-text')}
                  onButtonClick={() => {
                    history.push(
                      `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_SUBMIT_EXPENSE}?planPeriodId=${planPeriodId}&expenseCategoryId=${item.id}`,
                    )
                  }}
                  monthlyAllowanceValue={item.monthlyAllowance}
                  startDate={item.startDate}
                  endDate={item.endDate}
                  type={allowanceFrequencies.find((x) => x.id == item.allowanceFrequency)?.name}
                  eligibleCategories={item.eligibleExpenseTypes}
                  isEligible={categoriesSummary.isEligible}
                  offerSameAllowance={categoriesSummary.offerSameAllowance}
                  benefitEndDate={endDate}
                />
              ))}
            </Carousel>
          </>
        )}
      </Skeleton>
    </>
  )
}

export default PerksAllowance
